import {useEffect} from 'react';
import {
	hasNovaOutputBeenViewed,
	toNovaOutcome,
	trackNovaOutputViewed,
} from '../../../../../../tracking/amplitude/novaTaskAgentEvents';
import {findScrollContainer} from './centreInScrollContainer';

// How long the view has to stay put before what is on screen counts as seen. Scrolling past a reply
// on the way somewhere else is not reading it.
export const NOVA_OUTPUT_STILL_MS = 600;

type VerticalSpan = {top: number; bottom: number};

// Seen means the whole answer card is on screen, or - for a card taller than the space it has - its
// end is, which is where reading it finishes.
export function isAnswerCardInView(card: VerticalSpan, visible: VerticalSpan): boolean {
	const visibleHeight = visible.bottom - visible.top;
	// A card with no height is not drawn (display: none measures 0 x 0), whatever its position says.
	if (visibleHeight <= 0 || card.bottom - card.top <= 0) return false;
	const bottomInView = card.bottom >= visible.top && card.bottom <= visible.bottom;
	if (card.bottom - card.top > visibleHeight) return bottomInView;
	return bottomInView && card.top >= visible.top;
}

// The part of the screen the card can be seen through: its scroll container (the task modal body)
// clipped to the window, or the window itself when nothing scrolls it.
function visibleSpan(container: HTMLElement | null): VerticalSpan {
	const windowSpan = {top: 0, bottom: window.innerHeight};
	if (!container) return windowSpan;
	const box = container.getBoundingClientRect();
	return {top: Math.max(box.top, windowSpan.top), bottom: Math.min(box.bottom, windowSpan.bottom)};
}

/**
 * Reports Nova Task Agent Output Viewed for a turn once a person has seen its answer card: the
 * conversation is expanded, the card is in view, the view has been still for NOVA_OUTPUT_STILL_MS
 * and the tab is visible. Any scroll, resize or return to the tab starts the wait again. Reporting is
 * once per run per page load, which the tracking module enforces.
 */
export function useNovaOutputViewed({
	card,
	turn,
	taskId,
	isCollapsed,
}: {
	card: HTMLElement | null;
	turn: {runId: string | null; outcome: string | null} | null;
	taskId: string;
	isCollapsed: boolean;
}): void {
	const runId = turn?.runId ?? null;
	const outcome = turn?.outcome ?? null;

	useEffect(() => {
		if (!card || !runId || isCollapsed || !toNovaOutcome(outcome)) return undefined;
		// Already sent in this page load (the task was reopened): nothing left to watch for.
		if (hasNovaOutputBeenViewed(runId)) return undefined;
		let timer: ReturnType<typeof setTimeout> | null = null;
		// Every listener is added with this signal, so one abort detaches them all.
		const listening = new AbortController();
		const stopListening = (): void => {
			if (timer !== null) clearTimeout(timer);
			timer = null;
			listening.abort();
		};

		// The scroll container is looked up at the moment of measuring, not once up front: the modal
		// body may only start to overflow after the reply first renders, once content above it loads.
		const check = (): void => {
			timer = null;
			if (document.visibilityState !== 'visible') return;
			if (isAnswerCardInView(card.getBoundingClientRect(), visibleSpan(findScrollContainer(card)))) {
				trackNovaOutputViewed({runId, taskId, outcome});
				// Reported once per run, so every later scroll would be measured for nothing.
				stopListening();
			}
		};
		const restart = (): void => {
			if (timer !== null) clearTimeout(timer);
			timer = setTimeout(check, NOVA_OUTPUT_STILL_MS);
		};
		const onVisibilityChange = (): void => {
			if (document.visibilityState === 'visible') restart();
		};

		// Scroll does not bubble, so it is caught on the way down instead: one capturing listener hears
		// the modal body, any other scrolling ancestor and the page itself.
		const {signal} = listening;
		document.addEventListener('scroll', restart, {capture: true, passive: true, signal});
		window.addEventListener('resize', restart, {signal});
		document.addEventListener('visibilitychange', onVisibilityChange, {signal});
		restart();

		return stopListening;
	}, [card, runId, outcome, taskId, isCollapsed]);
}
